import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { useState } from "react";
import { css } from "styled-components/macro"; //eslint-disable-line
import {
  SectionHeading,
  Subheading as SubheadingBase,
} from "components/misc/Headings.js";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import EmailIllustrationSrc from "images/giftideas.png";

const Container = tw.div`relative`;
const TwoColumn = tw.div`flex flex-col md:flex-row justify-between max-w-screen-xl mx-auto py-4 md:py-8`;
const Column = tw.div`w-full max-w-md mx-auto md:max-w-none md:mx-0`;
const ImageColumn = tw(Column)`md:w-5/12 flex-shrink-0 h-80 md:h-auto`;
const TextColumn = styled(Column)((props) => [
  tw`md:w-7/12 mt-16 md:mt-0`,
  props.textOnLeft
    ? tw`md:mr-12 lg:mr-16 md:order-first`
    : tw`md:ml-12 lg:ml-16 md:order-last`,
]);

const Image = styled.div((props) => [
  `background-image: url("${props.imageSrc}");`,
  tw`rounded bg-contain bg-no-repeat bg-center h-full max-w-[70%]`,
]);
const TextContent = tw.div`lg:py-8 text-center md:text-left`;

const Subheading = tw(SubheadingBase)`text-center md:text-left`;
const Heading = tw(
  SectionHeading
)`mt-4 font-black text-left text-3xl sm:text-4xl lg:text-5xl text-center md:text-left leading-tight`;
const Description = tw.p`mt-4 text-center md:text-left text-sm md:text-base lg:text-lg font-medium leading-relaxed text-secondary-100`;

const Form = tw.form`mt-8 md:mt-10 text-sm flex flex-col max-w-sm mx-auto md:mx-0`;
const Input = tw.input`mt-6 first:mt-0 border-b-2 py-3 focus:outline-none font-medium transition duration-300 hocus:border-primary-500`;

const Label = tw.label`mx-2 mt-4 text-center md:text-left text-sm md:text-base lg:text-lg font-medium leading-relaxed text-secondary-100`;

const Textarea = styled(Input).attrs({ as: "textarea" })`
  ${tw`h-24`}
`;

const SubmitButton = tw(PrimaryButtonBase)`inline-block mt-8`;

export default ({
  subheading = "",
  heading = (
    <>
      Start searching for <span tw="text-primary-500">Gifts!</span>
      <wbr />
    </>
  ),
  description = "Fill out the form for the person you are buying gifts for. Although all fields are optional, we recommend providing as much information as possible for a more personalized gift selection.",
  submitButtonText = "Find Gifts",
  textOnLeft = true,
  searchFor,
}) => {
  // The textOnLeft boolean prop can be used to display either the text on left or right side of the image.
  const handleChange = (evt) => {
    const { name, value } = evt.target;
    setFormData((formData) => ({
      ...formData,
      [name]: value,
    }));
  };
  const handleCheckBoxChange = (e) => {
    // to find out if it's checked or not; returns true or false
    const checked = e.target.checked;
    // to get the checked value
    const checkedValue = e.target.value;

    if (checked) {
      formData.letter = checkedValue;
    } else {
      formData.letter = !checkedValue;
    }
  };

  const [formData, setFormData] = useState({
    relationship: "",
    occasion: "",
    hobbies: "",
    gender: "",
    age: "",
    price: "",
    others: "",
    letter: false,
  });

  const handleSubmit = (evt) => {
    evt.preventDefault();

    searchFor({ ...formData });
    setFormData({
      relationship: "",
      occasion: "",
      hobbies: "",
      gender: "",
      age: "",
      price: "",
      others: "",
      letter: false,
    });
  };

  return (
    <Container>
      <TwoColumn>
        <ImageColumn>
          <Image imageSrc={EmailIllustrationSrc} />
        </ImageColumn>
        <TextColumn textOnLeft={textOnLeft}>
          <TextContent>
            {subheading && <Subheading>{subheading}</Subheading>}
            <Heading>{heading}</Heading>
            {description && <Description>{description}</Description>}

            <Form onSubmit={handleSubmit}>
              <Description>Relationship to the person</Description>
              <Input
                name="relationship"
                value={formData.relationship}
                type="input"
                placeholder="Dad, Wife, Collegue, etc."
                onChange={handleChange}
                required
              />

              <Description>
                What is the special occasion/upcoming event?
              </Description>
              <Input
                name="occasion"
                value={formData.occasion}
                type="input"
                placeholder="Anniversary, Birthday, Christmas, etc."
                onChange={handleChange}
                required
              />

              <Description>What are their hobbies?</Description>
              <Input
                name="hobbies"
                value={formData.hobbies}
                type="input"
                placeholder="Reading, Hiking, Soccer, etc."
                onChange={handleChange}
              />

              <Description>Max Price ($)</Description>
              <Input
                name="price"
                value={formData.price}
                type="number"
                placeholder="Maximum price limit for the gift"
                min={0}
                onChange={handleChange}
              />

              <Description>What is their age?</Description>
              <Input
                name="age"
                value={formData.age}
                type="number"
                placeholder="27"
                min={0}
                max={100}
                onChange={handleChange}
              />

              <Description>Other information related to person</Description>
              <Textarea
                name="others"
                value={formData.others}
                type="text"
                placeholder="He is a big fan of Game of Thrones.&#13;&#10;She loves chocolates."
                as="textarea"
                onChange={handleChange}
              />

              <Container>
                <Description>What is their gender?</Description>
                <Input
                  id="male"
                  name="gender"
                  type="radio"
                  value="male"
                  onChange={handleChange}
                  className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
                <Label htmlFor="male">Male</Label>
                <Input
                  id="female"
                  name="gender"
                  type="radio"
                  value="female"
                  onChange={handleChange}
                  className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
                <Label htmlFor="female">Female</Label>
                <Input
                  id="na"
                  name="gender"
                  type="radio"
                  value="N/A"
                  onChange={handleChange}
                  className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
                <Label htmlFor="na">Non-Binary</Label>
              </Container>

              <br></br>

              {/* <Container>
                <Input
                  id="thankyou"
                  type="checkbox"
                  name="letter"
                  value={true}
                  onChange={handleCheckBoxChange}
                />
                <Label htmlFor="thankyou">
                  Do you want a thank you letter?
                </Label>
              </Container> */}
              <SubmitButton type="submit" className="find-gifts-btn">
                {submitButtonText}
              </SubmitButton>
            </Form>
          </TextContent>
        </TextColumn>
      </TwoColumn>
    </Container>
  );
};
